/**
 * Session Summary Configuration Panel
 *
 * Configure the system-wide default prompt used by SessionSummaryManager
 * to compress long conversations into short summaries. Per-agent
 * custom_prompt (in agent config) takes precedence over this system default.
 */

import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Loading } from "@/components/common";
import { AlertCircle, AlertTriangle, RotateCcw, Save } from "lucide-react";
import {
  useSessionSummarySystemConfig,
  useUpdateSessionSummarySystemConfig,
} from "../hooks/useSystemConfig";
import { systemApi } from "@/lib/api/system";
import type { SessionSummarySystemConfig } from "@/types/system";

export const SessionSummaryConfigPanel: React.FC = () => {
  const { data: config, isLoading, error, refetch } = useSessionSummarySystemConfig();
  const updateConfig = useUpdateSessionSummarySystemConfig();

  const [summaryPrompt, setSummaryPrompt] = useState("");

  useEffect(() => {
    if (config) {
      setSummaryPrompt(config.summary_prompt || "");
    }
  }, [config]);

  const handleSave = async () => {
    const trimmed = summaryPrompt.trim();
    if (trimmed.length < 50) {
      alert("Prompt 至少需要 50 个字符");
      return;
    }
    if (trimmed.length > 8000) {
      alert("Prompt 不能超过 8000 个字符");
      return;
    }

    try {
      const newConfig: SessionSummarySystemConfig = { summary_prompt: trimmed };
      await updateConfig.mutateAsync(newConfig);
      alert("保存成功");
    } catch (err) {
      alert(`保存失败: ${err}`);
    }
  };

  const handleResetToHardcoded = async () => {
    try {
      const defaults = await systemApi.sessionSummary.getDefaults();
      if (defaults.summary_prompt) {
        setSummaryPrompt(defaults.summary_prompt);
        alert("已重置为硬编码默认值");
      } else {
        alert("未能获取默认值");
      }
    } catch (err) {
      alert(`重置失败: ${(err as Error).message}`);
    }
  };

  if (isLoading) {
    return <Loading message="加载中..." />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>加载失败: {error.message}</span>
      </div>
    );
  }

  const hasChanges = summaryPrompt !== (config?.summary_prompt || "");

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              修改此 prompt 会影响所有开启 Session Summary 的 Agent
              （除非该 Agent 在自己的配置中设置了 custom_prompt）。保存后立即生效。
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Session Summary 配置
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            SessionSummaryManager 生成会话摘要时使用的系统级默认 prompt。用于将长对话压缩成简短摘要，节省上下文 token。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={updateConfig.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            重新加载
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateConfig.isPending}
          >
            <Save className="w-4 h-4 mr-2" />
            {updateConfig.isPending ? "保存中..." : "保存配置"}
          </Button>
        </div>
      </div>

      {/* Summary Prompt */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700 flex items-center">
            <span className="w-4 h-4 mr-2 text-indigo-500">📝</span>
            摘要生成 Prompt
          </label>
          <button
            type="button"
            onClick={handleResetToHardcoded}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />
            重置为硬编码默认值
          </button>
        </div>
        <Textarea
          value={summaryPrompt}
          onChange={(e) => setSummaryPrompt(e.target.value)}
          rows={14}
          className="font-mono text-sm"
        />
        <p className="mt-2 text-xs text-gray-500">
          最少 50 字符，最多 8000 字符。建议在 Agno 内置 prompt 基础上追加语言指令
          （例如"Write the summary in the SAME LANGUAGE as the conversation"），
          让摘要自动跟随用户语言。Agno 会在此 prompt 后面追加对话内容与 JSON schema
          指令。
        </p>
      </Card>

      {/* Metadata */}
      {config?.version !== undefined && (
        <div className="text-sm text-gray-500">
          <p>
            版本: <span>{config.version}</span> | 更新者:{" "}
            <span>{config.updated_by || "N/A"}</span> |{" "}
            <span>
              {config.updated_at
                ? new Date(config.updated_at).toLocaleString()
                : "N/A"}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default SessionSummaryConfigPanel;
